import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AllowanceAssignmentsPanel } from "../components/AllowanceAssignmentsPanel";
import { GradeEntitlementForm } from "../components/GradeEntitlementForm";
import { ErrorPanel, Loading } from "../components/Loading";
import { api, ApiError } from "../lib/api";

// Grade detail page (M13.4) — header + entitlement bundle with
// inheritance indicators. Edits save via PATCH and invalidate both
// the grade and its resolved-bundle query.

export function GradeDetailPage() {
  const { t } = useTranslation();
  const { gradeId } = useParams<{ gradeId: string }>();
  const qc = useQueryClient();

  const gradeQ = useQuery({
    queryKey: ["grade", gradeId],
    queryFn: () => api.getGrade(gradeId!),
    enabled: !!gradeId,
  });
  const resolvedQ = useQuery({
    queryKey: ["grade-resolved", gradeId],
    queryFn: () => api.getGradeResolved(gradeId!),
    enabled: !!gradeId,
  });
  const parentQ = useQuery({
    queryKey: ["grade", gradeQ.data?.parent_grade_id],
    queryFn: () => api.getGrade(gradeQ.data!.parent_grade_id!),
    enabled: !!gradeQ.data?.parent_grade_id,
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.updateGrade(gradeId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grade", gradeId] });
      qc.invalidateQueries({ queryKey: ["grade-resolved", gradeId] });
      qc.invalidateQueries({ queryKey: ["grades"] });
    },
  });

  if (gradeQ.isLoading || resolvedQ.isLoading) return <Loading />;
  if (gradeQ.error)
    return <ErrorPanel message={(gradeQ.error as Error).message} />;
  if (resolvedQ.error)
    return <ErrorPanel message={(resolvedQ.error as Error).message} />;
  if (!gradeQ.data || !resolvedQ.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Layers className="h-3.5 w-3.5" />
              {t("policies.grades_label_prefix", { code: gradeQ.data.code })}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-text-primary">
              {gradeQ.data.name_en}
              <span
                className="ms-2 text-base font-normal text-text-tertiary"
                dir="rtl"
              >
                {gradeQ.data.name_ar}
              </span>
            </h1>
            {parentQ.data && (
              <p className="mt-2 text-sm text-slate-600">
                {t("policies.grades_parent_label")}{" "}
                <code className="font-mono text-xs">{parentQ.data.code}</code>{" "}
                — {parentQ.data.name_en}
              </p>
            )}
            {gradeQ.data.description && (
              <p className="mt-2 text-sm italic text-slate-600">
                {gradeQ.data.description}
              </p>
            )}
          </div>
          <span className="text-xs text-slate-400">
            {t("policies.grades_resolution_chain", { count: resolvedQ.data.chain.length })}
          </span>
        </div>
      </div>

      {mutation.error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {mutation.error instanceof ApiError
            ? mutation.error.detail
            : String(mutation.error)}
        </div>
      )}

      <GradeEntitlementForm
        grade={gradeQ.data}
        resolved={resolvedQ.data}
        parentGradeName={parentQ.data?.name_en ?? null}
        onFieldChange={(field, value) => mutation.mutate({ [field]: value })}
      />

      <AllowanceAssignmentsPanel
        layer="grade"
        parentId={gradeQ.data.id}
        legalEntityId={gradeQ.data.legal_entity_id}
      />
    </div>
  );
}
